import { Box, TextField as Input, Theme } from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import clsx from "clsx";
import * as React from "react";

import AddValueButton from "./AddValueButton";
import EditButton from "./EditButton";

const useStyles = makeStyles((theme: Theme) => ({
  wrapper: {
    width: "100%",
    maxWidth: "100%",
    height: "100%",
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(2),
    margin: theme.spacing(-2),

    "&:hover $editButton": {
      display: "block",
    },
  },

  text: {
    textOverflow: "ellipsis",
    overflow: "hidden",
    whiteSpace: "nowrap",
    flex: 1,
  },

  editButton: {},

  inputField: {
    width: "100%",
    height: 32,
    padding: theme.spacing(0.5),
  },
}));

interface TextFieldProps {
  value: string;
  fieldName: string;
  onEdit: (newVal: string) => void;
  multiline?: boolean;
}

export default React.memo(function TextField({
  value,
  onEdit,
  fieldName,
  multiline,
}: TextFieldProps) {
  const [isEditing, setEditing] = React.useState(false);
  const [editValue, setEditValue] = React.useState(value);
  const classes = useStyles();

  const handleKeyDown: React.KeyboardEventHandler<HTMLInputElement> = React.useCallback(
    (e) => {
      if (e.key === "Enter" && !e.shiftKey) {
        onEdit(editValue);
        setEditing(false);
      } else if (e.key === "Escape") {
        setEditing(false);
        setEditValue(value);
      }
    },
    [onEdit, editValue, value]
  );

  return (
    <Box className={clsx(classes.wrapper)}>
      {isEditing ? (
        <Input
          className={clsx(classes.inputField)}
          defaultValue={value}
          autoFocus
          onBlur={() => {
            onEdit(editValue);
            setEditing(false);
          }}
          multiline={multiline}
          onKeyDown={handleKeyDown}
          onChange={(e) => setEditValue(e.target.value)}
          onClick={(e) => e.stopPropagation()}
        />
      ) : value ? (
        <Box title={value} className={clsx(classes.text)}>
          {value}
        </Box>
      ) : (
        <AddValueButton
          fieldName={fieldName}
          onClick={(e) => {
            // we stop propagation of event to prevent that
            // clicks on buttons trigger clicks on the row
            e.stopPropagation();
            setEditing(true);
          }}
        />
      )}

      {!isEditing && value && (
        <EditButton
          className={classes.editButton}
          onClick={(e) => {
            // we stop propagation of event to prevent that
            // clicks on buttons trigger clicks on the row
            e.stopPropagation();
            setEditing(true);
          }}
        />
      )}
    </Box>
  );
});
